import { Component, ReactNode, useEffect, useRef, useState } from "react";
import { DownloadSimple, File } from "@phosphor-icons/react";

import { VideoPlayer } from "@/ui/VideoPlayer";
import { parseCsv } from "./csv";
import { loadPdfjs } from "./pdfjs";
import { previewKind, PreviewKind } from "./previewKind";

// Shared preview library (Launch Media v1, SM-3). One component previews any file, choosing the
// viewer by type; other apps use it too. `url` is a link the browser can open (Launch Media hands
// out short-lived private links). Viewers come from what Launch OS already had: PDF.js (from the
// Launch Reviews book reader), the app's VideoPlayer, and Reviews' playback speeds.

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2, 2.5]; // same as Launch Reviews

// pages stacked in a scrolling column (like a PDF reader); the counter follows the scroll, and the
// arrows (or ← → keys) move a page at a time. Renders up to 60 pages, one after another.
export const PdfPages = ({ url }: { url: string }) => {
  const scroller = useRef<HTMLDivElement>(null);
  const pagesBox = useRef<HTMLDivElement>(null); // drawn pages only: React renders nothing inside it
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true;
    setCount(0); setPage(1); setErr("");
    const box = pagesBox.current; if (box) box.replaceChildren();
    loadPdfjs()
      .then((pdfjs) => pdfjs.getDocument({ url }).promise)
      .then(async (doc: any) => {
        if (!live || !box) return;
        const n = Math.min(doc.numPages, 60);
        setCount(doc.numPages);
        const width = (scroller.current?.clientWidth || 340) - 24, dpr = Math.min(2, window.devicePixelRatio || 1);
        for (let i = 1; i <= n && live; i++) {
          const p = await doc.getPage(i);
          const vp = p.getViewport({ scale: width / p.getViewport({ scale: 1 }).width });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(vp.width * dpr); canvas.height = Math.floor(vp.height * dpr);
          canvas.style.cssText = "width:100%;display:block;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,.35)";
          canvas.dataset.page = String(i);
          box.appendChild(canvas);
          await p.render({ canvasContext: canvas.getContext("2d"), viewport: vp, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined }).promise;
        }
      })
      .catch(() => live && setErr("This PDF couldn't be opened here. Download it to read it."));
    return () => { live = false; };
  }, [url]);
  const onScroll = () => {
    const box = scroller.current, pages = pagesBox.current; if (!box || !pages) return;
    const mid = box.scrollTop + box.clientHeight / 3;
    let current = 1;
    for (const c of Array.from(pages.children) as HTMLElement[]) if (c.offsetTop <= mid) current = Number(c.dataset.page) || current;
    setPage(current);
  };
  const go = (n: number) => {
    const target = pagesBox.current?.querySelector<HTMLElement>(`[data-page="${Math.max(1, Math.min(count, n))}"]`);
    if (target && scroller.current) scroller.current.scrollTo({ top: target.offsetTop - 12, behavior: "smooth" });
  };
  if (err) return <p className="p-4 text-sm text-black/50 dark:text-white/50">{err}</p>;
  return (
    <div style={{ background: "#525659" }} tabIndex={0} onKeyDown={(e) => { if (e.key === "ArrowRight" || e.key === "ArrowDown") { go(page + 1); e.preventDefault(); } if (e.key === "ArrowLeft" || e.key === "ArrowUp") { go(page - 1); e.preventDefault(); } }} aria-label="PDF pages">
      <div ref={scroller} onScroll={onScroll} style={{ position: "relative", maxHeight: 420, overflowY: "auto", padding: 12 }}>
        {!count && <p className="text-sm" style={{ color: "#e5e7eb" }}>Opening the PDF…</p>}
        <div ref={pagesBox} style={{ display: "flex", flexDirection: "column", gap: 12 }} />
      </div>
      {count > 0 && (
        <div className="flex items-center justify-center gap-3 text-xs" style={{ color: "#fff", padding: "6px 0 8px" }}>
          <button type="button" style={{ padding: "1px 8px", borderRadius: 6, background: "rgba(255,255,255,.15)" }} disabled={page <= 1} onClick={() => go(page - 1)} aria-label="Previous page">‹</button>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>Page {page} of {count}</span>
          <button type="button" style={{ padding: "1px 8px", borderRadius: 6, background: "rgba(255,255,255,.15)" }} disabled={page >= count} onClick={() => go(page + 1)} aria-label="Next page">›</button>
        </div>
      )}
    </div>
  );
};

export const AudioPreview = ({ url }: { url: string }) => {
  const el = useRef<HTMLAudioElement>(null);
  const [speed, setSpeed] = useState(1);
  useEffect(() => { if (el.current) el.current.playbackRate = speed; }, [speed]);
  return (
    <div className="p-4 flex flex-col gap-3">
      <audio ref={el} src={url} controls preload="metadata" className="w-full" />
      <label className="self-end text-xs text-black/50 dark:text-white/50 flex items-center gap-2">Speed
        <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="rounded border border-black/10 dark:border-white/20 bg-transparent px-1 py-0.5">
          {SPEEDS.map((s) => <option key={s} value={s}>{s}×</option>)}
        </select>
      </label>
    </div>
  );
};

const TextPreview = ({ url, kind, name }: { url: string; kind: "text" | "table"; name: string }) => {
  const [text, setText] = useState<string | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true; setText(null); setErr("");
    fetch(url).then((r) => (r.ok ? r.text() : Promise.reject())).then((t) => live && setText(t.slice(0, 200000))).catch(() => live && setErr("This file couldn't be opened here. Download it to read it."));
    return () => { live = false; };
  }, [url]);
  if (err) return <p className="p-4 text-sm text-black/50 dark:text-white/50">{err}</p>;
  if (text === null) return <p className="p-4 text-sm text-black/50 dark:text-white/50">Opening…</p>;
  if (kind === "text") return <pre className="p-4 text-xs leading-relaxed whitespace-pre-wrap break-words max-h-96 overflow-y-auto">{text}</pre>;
  const rows = parseCsv(text, 200, name.toLowerCase().endsWith(".tsv") ? "\t" : ",");
  return (
    <div className="max-h-96 overflow-auto">
      <table className="text-xs border-collapse min-w-full">
        <thead><tr>{(rows[0] || []).map((h, i) => <th key={i} className="sticky top-0 bg-white dark:bg-[#262626] border border-black/10 dark:border-white/10 px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{rows.slice(1).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="border border-black/10 dark:border-white/10 px-2 py-1 whitespace-nowrap">{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};

export const NoPreview = ({ onDownload }: { onDownload?: () => void }) => (
  <div className="p-6 flex flex-col items-center gap-2 text-center text-sm text-black/50 dark:text-white/50">
    <File size={36} />
    <p>There's no preview for this kind of file.</p>
    {onDownload && <button type="button" onClick={onDownload} className="flex items-center gap-1 font-semibold text-blue-600 dark:text-blue-300"><DownloadSimple size={14} /> Download to open it</button>}
  </div>
);

export const viewerLabel: Record<PreviewKind, string> = {
  picture: "Picture", pdf: "PDF viewer", video: "Video player", audio: "Audio player", table: "Table", text: "Text", none: "No preview",
};

// a viewer that fails shows a plain message in its own space; it never takes the whole page down
class PreviewBoundary extends Component<{ children: ReactNode; resetKey: string; onDownload?: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidUpdate(prev: { resetKey: string }) { if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false }); }
  componentDidCatch(err: unknown) { console.warn("preview failed", err); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="p-6 flex flex-col items-center gap-2 text-center text-sm text-black/50 dark:text-white/50">
        <p>This file couldn't be shown here.</p>
        {this.props.onDownload && <button type="button" onClick={this.props.onDownload} className="font-semibold text-blue-600 dark:text-blue-300">Download it instead</button>}
      </div>
    );
  }
}

export const FilePreview = (props: { type: string; name: string; url: string | null; onDownload?: () => void }) => (
  <PreviewBoundary resetKey={`${props.name}|${props.url}`} onDownload={props.onDownload}><Viewer {...props} /></PreviewBoundary>
);

const Viewer = ({ type, name, url, onDownload }: { type: string; name: string; url: string | null; onDownload?: () => void }) => {
  const kind = previewKind(type, name);
  if (kind === "none") return <NoPreview onDownload={onDownload} />;
  if (!url) return <p className="p-4 text-sm text-black/50 dark:text-white/50">Opening…</p>;
  if (kind === "picture") return <img src={url} alt={name} className="w-full max-h-96 object-contain bg-black/5 dark:bg-white/5" />;
  if (kind === "pdf") return <PdfPages url={url} />;
  if (kind === "video") return <VideoPlayer videoId={null} url={url} autoPlay={false} playerProps={{ controls: true, muted: false, loop: false, height: "auto", style: { aspectRatio: "16 / 9", background: "#000" } }} />;
  if (kind === "audio") return <AudioPreview url={url} />;
  return <TextPreview url={url} kind={kind} name={name} />;
};
